import { ICSVRow } from 'interfaces/ICSV';
import { IPlayWithBallFinal, IMergedPlayWithBallFinal } from 'interfaces/sessionVerification/plays/IPlayWithBallFinal';
import { ISession } from 'interfaces/session/ISession';
import moment from 'moment';
import { emptyGuid, undefinedValue, emptyPlayer } from 'constants/Constants';
import { TopBottom, IPlayMetadata, IApiPlayer, IPlayer, baseballLogicService } from '@trackman/baseball-shared-components';
import { ValueMerger } from './ValueMerger';
import { csvImportUtils } from '../../services/helper-services/csvImportUtils';

export const mergeRowAndPlay = (
  row: ICSVRow,
  playWithBf: IPlayWithBallFinal,
  session: ISession,
  homeTeamRoster: IPlayer[],
  awayTeamRoster: IPlayer[]
) => {
  const { play } = playWithBf;
  const merger = new ValueMerger();
  return {
    play: {
      createdInStadiumUtc: play.createdInStadiumUtc,
      createdInStadiumLocal: play.createdInStadiumLocal,
      updatedInStadiumUtc: play.updatedInStadiumLocal,
      updatedInStadiumLocal: play.updatedInStadiumLocal,
      trackStartTimeUtc: merger.mergeDates(row.utcdatetime, play.trackStartTimeUtc, 'trackStartTimeUtc'),
      trackStartTimeLocal: merger.merge(row.localdatetime, play.trackStartTimeLocal, 'trackStartTimeLocal'),
      id: play.id,
      time: moment().utc().toJSON(),
      version: play.version,
      sessionId: play.sessionId,
      playId: play.playId,
      taggerBehavior: {
        manuallyAddedByTagger: play.taggerBehavior.manuallyAddedByTagger,
        isWarmup: merger.merge(false, play.taggerBehavior.isWarmup, 'isWarmup'),
        playSequenceNumber: play.taggerBehavior.playSequenceNumber,
        sequenceNumber: play.taggerBehavior.sequenceNumber,
        isDeleted: merger.merge(false, play.taggerBehavior.isDeleted, 'isDeleted'),
        pAofInning: play.taggerBehavior.pAofInning,
        pitchOfPA: play.taggerBehavior.pitchOfPA,
      },
      players: {
        batter: mergeBatter(row, play, homeTeamRoster, awayTeamRoster, merger),
        catcher: mergeCatcher(row, play, homeTeamRoster, awayTeamRoster, merger),
        pitcher: mergePitcher(row, play, homeTeamRoster, awayTeamRoster, merger),
      },
      gameState: {
        homeTeamScore: play.gameState.homeTeamScore,
        awayTeamScore: play.gameState.awayTeamScore,
        isHomeTeamBatting: merger.merge(row.topbottom !== TopBottom.Top, play.gameState.isHomeTeamBatting, 'isHomeTeamBatting'),
        inning: merger.merge(row.inning, play.gameState.inning, 'inningNumber'),
        topBottom: merger.merge(row.topbottom, play.gameState.topBottom, 'inningTopBottom'),
        balls: merger.merge(row.balls, play.gameState.balls, 'gameBalls'),
        strikes: merger.merge(row.strikes, play.gameState.strikes, 'gameStrikes'),
        outs: merger.merge(row.outs, play.gameState.outs, 'gameOuts'),
      },
      pitchTag: {
        ballFinalTrackId: play.pitchTag.ballFinalTrackId,
        pitcherSet: play.pitchTag.pitcherSet,
        pitchType: merger.merge(row.taggedpitchtype, play.pitchTag.pitchType, 'pitchType'),
        pitchCall: merger.merge(row.pitchcall, play.pitchTag.pitchCall, 'pitchCall'),
      },
      hitTag: getHitTag(row, play, merger),
      catcherThrowTag: getCatcherThrowTag(play),
      playResult: {
        outs: merger.merge(row.outsonplay, play.playResult.outs, 'playOuts'),
        runs: merger.merge(row.runsscored, play.playResult.runs, 'playRuns'),
        result: merger.merge(row.playresult, play.playResult.result, 'playResult'),
      },
      note: merger.merge(row.notes, play.note, 'note'),
      kOrBB: merger.merge(row.korbb, play.kOrBB, 'kOrBB'),
      pitchingTeam: merger.mergeTeam(row.pitcherteam, play.pitchingTeam, session, 'pitchingTeam'),
      battingTeam: merger.mergeTeam(row.batterteam, play.battingTeam, session, 'battingTeam'),
    },
    ballFinals: playWithBf.ballFinals,
    isChanged: merger.isChanged,
    isBallTrackingChanged: merger.isChanged,
    isPlayerTrackingChanged: merger.isChanged,
    isMarkedForDeletion: false,
    isCreated: false,
    changedValues: merger.changedValues,
  } as IMergedPlayWithBallFinal;
};

const getHitTag = (row: ICSVRow, play: IPlayMetadata, merger: ValueMerger) => {
  return {
    ballFinalTrackId: play.hitTag && play.hitTag.ballFinalTrackId,
    hitType: merger.merge(row.taggedhittype || undefinedValue, play.hitTag && play.hitTag.hitType, 'hitType'),
    hitQuality: play.hitTag ? play.hitTag.hitQuality : '',
  };
};

const getCatcherThrowTag = (play: IPlayMetadata) => {
  return {
    ballFinalTrackId: play.catcherThrowTag && play.catcherThrowTag.ballFinalTrackId,
    thrownPosition: play.catcherThrowTag ? play.catcherThrowTag.thrownPosition : undefinedValue,
    fielder: play.catcherThrowTag ? play.catcherThrowTag.fielder : { ...emptyPlayer },
  };
};

const mergeBatter = (row: ICSVRow, play: IPlayMetadata, homeTeamRoster: IPlayer[], awayTeamRoster: IPlayer[], merger: ValueMerger) => {
  const playBatter = play.players.batter;
  const isDifferent = isDifferentPlayer(row.batterid, playBatter);
  const batter = {
    id: playBatter.id,
    nameRef: merger.merge(row.batter, playBatter.nameRef, 'batterName'),
    foreignIds: playBatter.foreignIds,
    position: merger.merge(isDifferent ? undefinedValue : playBatter.position, playBatter.position, 'batterPosition'),
    battingHandedness: merger.merge(row.batterside, playBatter.battingHandedness, 'battingHandedness'),
    isPlaceholder: playBatter.isPlaceholder,
    // TODO: verify CSV
    height: isDifferent ? null : playBatter.height,
    strikeZoneBottom: isDifferent ? null : playBatter.strikeZoneBottom,
    strikeZoneTop: isDifferent ? null : playBatter.strikeZoneTop,
    jerseyNumber: isDifferent ? '' : playBatter.jerseyNumber,
  };

  if (isDifferent) {
    const roster = baseballLogicService.isHomeTeamBatting(row.topbottom === TopBottom.Top) ? homeTeamRoster : awayTeamRoster;
    handlePlayerChanged(batter, row.batterid, row.batter, roster, 'batter', merger);
  }
  return batter;
};

const mergeCatcher = (row: ICSVRow, play: IPlayMetadata, homeTeamRoster: IPlayer[], awayTeamRoster: IPlayer[], merger: ValueMerger) => {
  const playCatcher = play.players.catcher;
  const isDifferent = isDifferentPlayer(row.catcherid, playCatcher);
  const catcher = {
    id: playCatcher.id,
    nameRef: merger.merge(row.catcher, playCatcher.nameRef, 'catcherName'),
    foreignIds: playCatcher.foreignIds,
    position: merger.merge('C', playCatcher.position, 'catcherPosition'),
    isPlaceholder: playCatcher.isPlaceholder,
    pitchingHandedness: merger.merge(row.catcherthrows, playCatcher.pitchingHandedness, 'catcherHandedness'),
    // TODO: verify CSV
    height: isDifferent ? null : playCatcher.height,
    strikeZoneBottom: isDifferent ? null : playCatcher.strikeZoneBottom,
    strikeZoneTop: isDifferent ? null : playCatcher.strikeZoneTop,
    jerseyNumber: isDifferent ? '' : playCatcher.jerseyNumber,
  };

  if (isDifferent) {
    const roster = baseballLogicService.isHomeTeamBatting(row.topbottom === TopBottom.Top) ? awayTeamRoster : homeTeamRoster;
    handlePlayerChanged(catcher, row.catcherid, row.catcher, roster, 'catcher', merger);
  }
  return catcher;
};

const mergePitcher = (row: ICSVRow, play: IPlayMetadata, homeTeamRoster: IPlayer[], awayTeamRoster: IPlayer[], merger: ValueMerger) => {
  const playPitcher = play.players.pitcher;
  const isDifferent = isDifferentPlayer(row.pitcherid, playPitcher);
  const pitcher = {
    id: playPitcher.id,
    nameRef: merger.merge(row.pitcher, playPitcher.nameRef, 'pitcherName'),
    foreignIds: playPitcher.foreignIds,
    position: merger.merge('P', playPitcher.position, 'pitcherPosition'),
    isPlaceholder: playPitcher.isPlaceholder,
    pitchingHandedness: merger.merge(row.pitcherthrows, playPitcher.pitchingHandedness, 'pitchingHandedness'),
    // TODO: verify CSV
    height: isDifferent ? null : playPitcher.height,
    strikeZoneBottom: isDifferent ? null : playPitcher.strikeZoneBottom,
    strikeZoneTop: isDifferent ? null : playPitcher.strikeZoneTop,
    jerseyNumber: isDifferent ? '' : playPitcher.jerseyNumber,
  };
  if (isDifferent) {
    const roster = baseballLogicService.isHomeTeamBatting(row.topbottom === TopBottom.Top) ? awayTeamRoster : homeTeamRoster;
    handlePlayerChanged(pitcher, row.pitcherid, row.pitcher, roster, 'pitcher', merger);
  }
  return pitcher;
};

const handlePlayerChanged = (
  player: IApiPlayer,
  csvId: string,
  csvName: string,
  roster: IPlayer[],
  playerTypePrefix: 'batter' | 'pitcher' | 'catcher',
  merger: ValueMerger
) => {
  const rosterPlayer = csvImportUtils.findPlayerInRoster(roster, csvName, csvId);
  player.isPlaceholder = merger.merge(false, player.isPlaceholder, `${playerTypePrefix}IsPlaceholder`);
  if (rosterPlayer) {
    player.id = merger.merge(rosterPlayer.id, player.id, `${playerTypePrefix}Id`);
    player.foreignIds = merger.merge(rosterPlayer.foreignIds, player.foreignIds, `${playerTypePrefix}ForeignIds`);
  } else {
    player.id = merger.merge(emptyGuid, player.id, `${playerTypePrefix}Id`);
    player.foreignIds = merger.merge([csvId], player.foreignIds, `${playerTypePrefix}ForeignIds`);
  }
};
const isDifferentPlayer = (csvId: string, player: IApiPlayer) => {
  if (!csvId && player.foreignIds.length === 0) {
    return false;
  }
  return !csvId || player.foreignIds.every((id) => !csvImportUtils.isForeignIdMatch(id, csvId));
};
